import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from './axiosConfig';
import { generateLessonPlan, normalizeAiError } from './services/ai.js';
import { CAPS_GRADES, CAPS_SUBJECTS } from './utils/capsOptions.js';
import './LessonPlans.css';

const STORAGE_KEY = 'marksure_lesson_plan_draft';
const WORKSHEET_DRAFT_KEY = 'marksure_worksheet_draft';

const createDefaultForm = () => ({
    schoolName: '',
    subject: '',
    grade: '',
    year: String(new Date().getFullYear()),
    term: '',
    weekNumber: '',
    dateRange: '',
    mainTopic: '',
    lessonCount: 4,
    pageReference: '',
    additionalContext: '',
    classesText: ''
});

function normalizeClasses(classesText) {
    return String(classesText || '')
        .split(/[\n,]+/)
        .map((item) => item.trim())
        .filter(Boolean);
}

function parseStoredDraft() {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') {
            return null;
        }

        return parsed;
    } catch {
        return null;
    }
}

function LoadingSkeleton({ lessonCount }) {
    return (
        <section className="lesson-plan-preview lesson-plan-skeleton" aria-label="Generating lesson plan">
            <div className="lesson-plan-skeleton-block lesson-plan-skeleton-header" />
            <div className="lesson-plan-skeleton-block lesson-plan-skeleton-toolbar" />
            <div className="lesson-plan-skeleton-grid">
                {Array.from({ length: Math.max(lessonCount, 4) }).map((_, index) => (
                    <div key={`lesson-skeleton-${index}`} className="lesson-plan-skeleton-column">
                        <div className="lesson-plan-skeleton-block lesson-plan-skeleton-title" />
                        <div className="lesson-plan-skeleton-block lesson-plan-skeleton-body" />
                        <div className="lesson-plan-skeleton-block lesson-plan-skeleton-body short" />
                        <div className="lesson-plan-skeleton-block lesson-plan-skeleton-body" />
                    </div>
                ))}
            </div>
        </section>
    );
}

export default function LessonPlans() {
    const navigate = useNavigate();
    const [form, setForm] = useState(createDefaultForm);
    const [plan, setPlan] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [saveMessage, setSaveMessage] = useState('');
    const [capsTopics, setCapsTopics] = useState([]);
    const [capsTopicsLoading, setCapsTopicsLoading] = useState(false);
    const [selectedCapsTopics, setSelectedCapsTopics] = useState([]);
    const [capsTopicNote, setCapsTopicNote] = useState('');

    useEffect(() => {
        const storedDraft = parseStoredDraft();
        if (!storedDraft) {
            return;
        }

        if (storedDraft.form && typeof storedDraft.form === 'object') {
            setForm((current) => ({ ...current, ...storedDraft.form }));
        }

        if (storedDraft.plan && typeof storedDraft.plan === 'object') {
            setPlan(storedDraft.plan);
        }
    }, []);

    useEffect(() => {
        if (!saveMessage) {
            return undefined;
        }

        const timeoutId = window.setTimeout(() => setSaveMessage(''), 2500);
        return () => window.clearTimeout(timeoutId);
    }, [saveMessage]);

    const parsedClasses = useMemo(() => normalizeClasses(form.classesText), [form.classesText]);

    const updateFormField = (field, value) => {
        setForm((current) => ({ ...current, [field]: value }));
    };

    const loadCapsTopics = async () => {
        if (!form.grade.trim() || !form.subject.trim() || !form.term.trim()) {
            setError('Select the grade, subject, and term before loading CAPS topics.');
            return;
        }

        setError('');
        setCapsTopicsLoading(true);
        try {
            const termMatch = form.term.match(/[1-4]/);
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/ai/topics.php`, {
                grade: form.grade,
                subject: form.subject,
                term: termMatch ? termMatch[0] : form.term
            }, { timeout: 70000 });
            setCapsTopics(Array.isArray(response.data?.topics) ? response.data.topics : []);
            setSelectedCapsTopics([]);
            setCapsTopicNote(response.data?.teacherNote || 'Confirm the final scope against your school ATP.');
        } catch (requestError) {
            setError(normalizeAiError(requestError));
        } finally {
            setCapsTopicsLoading(false);
        }
    };

    const toggleCapsTopic = (title) => {
        const next = selectedCapsTopics.includes(title)
            ? selectedCapsTopics.filter((item) => item !== title)
            : [...selectedCapsTopics, title];
        setSelectedCapsTopics(next);
        updateFormField('mainTopic', next.join(', '));
    };

    const handleGenerate = async (event) => {
        event.preventDefault();
        setLoading(true);
        setError('');

        try {
            const nextPlan = await generateLessonPlan({
                schoolName: form.schoolName,
                subject: form.subject,
                grade: form.grade,
                year: form.year,
                term: form.term,
                weekNumber: form.weekNumber,
                dateRange: form.dateRange,
                mainTopic: form.mainTopic,
                lessonCount: Number(form.lessonCount) || 4,
                pageReference: form.pageReference,
                additionalContext: form.additionalContext,
                classes: parsedClasses
            });

            setPlan(nextPlan);
            const nextDraft = {
                form: { ...form, classesText: parsedClasses.join(', ') },
                plan: nextPlan
            };
            localStorage.setItem(STORAGE_KEY, JSON.stringify(nextDraft));
            setSaveMessage('Lesson plan generated and draft saved.');
        } catch (requestError) {
            setError(normalizeAiError(requestError));
        } finally {
            setLoading(false);
        }
    };

    const handleSaveDraft = () => {
        const draftPayload = {
            form: { ...form, classesText: parsedClasses.join(', ') },
            plan
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(draftPayload));
        setSaveMessage('Draft saved on this device.');
    };

    const handleClearDraft = () => {
        localStorage.removeItem(STORAGE_KEY);
        setForm(createDefaultForm());
        setPlan(null);
        setError('');
        setSaveMessage('Saved draft cleared.');
    };

    const updatePlanField = (field, value) => {
        setPlan((current) => current ? { ...current, [field]: value } : current);
    };

    const updateLessonField = (index, field, value) => {
        setPlan((current) => {
            if (!current) {
                return current;
            }

            const nextLessons = current.lessons.map((lesson, lessonIndex) => (
                lessonIndex === index ? { ...lesson, [field]: value } : lesson
            ));

            return { ...current, lessons: nextLessons };
        });
    };

    const createWorksheetFromLesson = (lesson) => {
        const termMatch = String(plan?.term || form.term || '').match(/[1-4]/);
        const worksheetDraft = {
            grade: plan?.grade || form.grade,
            subject: plan?.subject || form.subject,
            term: termMatch ? termMatch[0] : '1',
            topic: lesson.title || form.mainTopic,
            difficulty: 'Core',
            totalMarks: 25,
            questionCount: 5,
            additionalContext: [
                `Lesson core knowledge: ${lesson.coreKnowledge || ''}`,
                `Lesson assessment: ${lesson.assessment || ''}`,
                `Lesson homework: ${lesson.homework || ''}`
            ].filter(Boolean).join('\n')
        };

        localStorage.setItem(WORKSHEET_DRAFT_KEY, JSON.stringify(worksheetDraft));
        navigate('/worksheets');
    };

    return (
        <div className="lesson-plan-page">
            <div className="lesson-plan-shell">
                <section className="lesson-plan-hero">
                    <div>
                        <p className="lesson-plan-kicker">AI Lesson Planner</p>
                        <h1>Generate CAPS-aligned weekly lesson plans</h1>
                        <p className="lesson-plan-intro">
                            Build a printable weekly plan, then edit every cell before saving it as a PDF from your browser&apos;s print dialog.
                        </p>
                    </div>
                    <div className="lesson-plan-hero-actions">
                        <button type="button" className="lesson-plan-secondary-btn" onClick={handleSaveDraft}>
                            Save Draft
                        </button>
                        <button
                            type="button"
                            className="lesson-plan-primary-btn"
                            onClick={() => window.print()}
                            disabled={!plan}
                        >
                            Print / Save PDF
                        </button>
                    </div>
                </section>

                <section className="lesson-plan-form-card">
                    <form className="lesson-plan-form" onSubmit={handleGenerate}>
                        <div className="lesson-plan-form-grid">
                            <label>
                                <span>School Name</span>
                                <input value={form.schoolName} onChange={(event) => updateFormField('schoolName', event.target.value)} />
                            </label>
                            <label>
                                <span>Subject</span>
                                <select required value={form.subject} onChange={(event) => updateFormField('subject', event.target.value)}>
                                    <option value="">Select subject</option>
                                    {!CAPS_SUBJECTS.includes(form.subject) && form.subject && <option value={form.subject}>{form.subject}</option>}
                                    {CAPS_SUBJECTS.map((subject) => <option key={subject} value={subject}>{subject}</option>)}
                                </select>
                            </label>
                            <label>
                                <span>Grade</span>
                                <select required value={form.grade} onChange={(event) => updateFormField('grade', event.target.value)}>
                                    <option value="">Select grade</option>
                                    {!CAPS_GRADES.some((grade) => grade.value === form.grade) && form.grade && <option value={form.grade}>{form.grade}</option>}
                                    {CAPS_GRADES.map((grade) => <option key={grade.value} value={grade.value}>{grade.label}</option>)}
                                </select>
                            </label>
                            <label>
                                <span>Year</span>
                                <input value={form.year} onChange={(event) => updateFormField('year', event.target.value)} />
                            </label>
                            <label>
                                <span>Term</span>
                                <input value={form.term} onChange={(event) => updateFormField('term', event.target.value)} placeholder="Term 3" />
                            </label>
                            <label>
                                <span>Week Number</span>
                                <input value={form.weekNumber} onChange={(event) => updateFormField('weekNumber', event.target.value)} placeholder="Week 6" />
                            </label>
                            <label>
                                <span>Date Range</span>
                                <input value={form.dateRange} onChange={(event) => updateFormField('dateRange', event.target.value)} placeholder="24 - 28 August 2026" />
                            </label>
                            <label>
                                <span>Number of Lessons</span>
                                <input
                                    type="number"
                                    min="1"
                                    max="8"
                                    value={form.lessonCount}
                                    onChange={(event) => updateFormField('lessonCount', event.target.value)}
                                />
                            </label>
                        </div>

                        <div className="lesson-plan-topic-field lesson-plan-form-span">
                        <label>
                            <span>Topic Description</span>
                            <textarea
                                rows={3}
                                value={form.mainTopic}
                                onChange={(event) => updateFormField('mainTopic', event.target.value)}
                                placeholder="Describe the weekly focus and the content you want covered."
                            />
                        </label>
                        <button type="button" className="lesson-plan-secondary-btn" onClick={loadCapsTopics} disabled={capsTopicsLoading}>
                            {capsTopicsLoading ? 'Finding topics...' : 'Get CAPS topics'}
                        </button>
                        </div>
                        {capsTopics.length > 0 && <div className="lesson-plan-caps-topics">
                            <strong>Suggested CAPS topics ({selectedCapsTopics.length} selected)</strong>
                            <div>{capsTopics.map((topic) => <button type="button" key={topic.title} className={selectedCapsTopics.includes(topic.title) ? 'selected' : ''} onClick={() => toggleCapsTopic(topic.title)}>{topic.title}<small>{topic.description}</small></button>)}</div>
                            {capsTopicNote && <p>{capsTopicNote}</p>}
                        </div>}

                        <div className="lesson-plan-form-grid lesson-plan-form-grid--secondary">
                            <label>
                                <span>Page Reference</span>
                                <input
                                    value={form.pageReference}
                                    onChange={(event) => updateFormField('pageReference', event.target.value)}
                                    placeholder="Pages 32-35, CAPS p. 74"
                                />
                            </label>
                            <label className="lesson-plan-form-span">
                                <span>Classes</span>
                                <textarea
                                    rows={2}
                                    value={form.classesText}
                                    onChange={(event) => updateFormField('classesText', event.target.value)}
                                    placeholder="8A, 8B, 8C"
                                />
                            </label>
                        </div>

                        <label className="lesson-plan-form-span">
                            <span>Extra Context for AI</span>
                            <textarea
                                rows={3}
                                value={form.additionalContext}
                                onChange={(event) => updateFormField('additionalContext', event.target.value)}
                                placeholder="Mention textbook, pace, barriers to learning, exam preparation goals, or specific CAPS focus areas."
                            />
                        </label>

                        <div className="lesson-plan-form-footer">
                            <div className="lesson-plan-form-hint">
                                <strong>{parsedClasses.length}</strong>
                                <span>class{parsedClasses.length === 1 ? '' : 'es'} included in this weekly plan.</span>
                            </div>
                            <div className="lesson-plan-form-actions">
                                <button type="button" className="lesson-plan-secondary-btn" onClick={handleClearDraft}>
                                    Clear
                                </button>
                                <button type="submit" className="lesson-plan-primary-btn" disabled={loading}>
                                    {loading ? 'Generating...' : 'Generate Lesson Plan'}
                                </button>
                            </div>
                        </div>
                    </form>
                    {saveMessage && <div className="lesson-plan-info">{saveMessage}</div>}
                    {error && <div className="lesson-plan-error">{error}</div>}
                </section>

                {loading ? (
                    <LoadingSkeleton lessonCount={Number(form.lessonCount) || 4} />
                ) : plan ? (
                    <section className="lesson-plan-preview">
                        <div className="lesson-plan-printable">
                            <div className="lesson-plan-header">
                                <div className="lesson-plan-header-grid">
                                    <label>
                                        <span>School</span>
                                        <input
                                            aria-label="School name"
                                            value={plan.schoolName}
                                            onChange={(event) => updatePlanField('schoolName', event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Subject</span>
                                        <input
                                            aria-label="Subject"
                                            value={plan.subject}
                                            onChange={(event) => updatePlanField('subject', event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Grade</span>
                                        <input
                                            aria-label="Grade"
                                            value={plan.grade}
                                            onChange={(event) => updatePlanField('grade', event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Year</span>
                                        <input
                                            aria-label="Year"
                                            value={plan.year}
                                            onChange={(event) => updatePlanField('year', event.target.value)}
                                        />
                                    </label>
                                    <label>
                                        <span>Term</span>
                                        <input
                                            aria-label="Term"
                                            value={plan.term}
                                            onChange={(event) => updatePlanField('term', event.target.value)}
                                        />
                                    </label>
                                </div>
                                <div className="lesson-plan-weekbar">
                                    <label>
                                        <span>Week</span>
                                        <input
                                            aria-label="Week number"
                                            value={plan.weekNumber}
                                            onChange={(event) => updatePlanField('weekNumber', event.target.value)}
                                        />
                                    </label>
                                    <label className="lesson-plan-weekbar-date">
                                        <span>Date Range</span>
                                        <input
                                            aria-label="Date range"
                                            value={plan.dateRange}
                                            onChange={(event) => updatePlanField('dateRange', event.target.value)}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div className="lesson-plan-table-wrap">
                                <table className="lesson-plan-table">
                                    <thead>
                                        <tr>
                                            <th>Section</th>
                                            {plan.lessons.map((lesson, index) => (
                                                <th key={`lesson-head-${lesson.lessonNumber}-${index}`}>Lesson {lesson.lessonNumber}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <th>Main Topic</th>
                                            {plan.lessons.map((lesson, index) => (
                                                <td key={`lesson-title-${lesson.lessonNumber}`}>
                                                    <textarea
                                                        aria-label={`Lesson ${lesson.lessonNumber} title`}
                                                        value={lesson.title}
                                                        onChange={(event) => updateLessonField(index, 'title', event.target.value)}
                                                        rows={3}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <th>Core Knowledge</th>
                                            {plan.lessons.map((lesson, index) => (
                                                <td key={`lesson-core-${lesson.lessonNumber}`}>
                                                    <textarea
                                                        aria-label={`Lesson ${lesson.lessonNumber} core knowledge`}
                                                        value={lesson.coreKnowledge}
                                                        onChange={(event) => updateLessonField(index, 'coreKnowledge', event.target.value)}
                                                        rows={10}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <th>Page</th>
                                            {plan.lessons.map((lesson, index) => (
                                                <td key={`lesson-page-${lesson.lessonNumber}`}>
                                                    <textarea
                                                        aria-label={`Lesson ${lesson.lessonNumber} page reference`}
                                                        value={lesson.pageReference}
                                                        onChange={(event) => updateLessonField(index, 'pageReference', event.target.value)}
                                                        rows={2}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <th>Homework</th>
                                            {plan.lessons.map((lesson, index) => (
                                                <td key={`lesson-homework-${lesson.lessonNumber}`}>
                                                    <textarea
                                                        aria-label={`Lesson ${lesson.lessonNumber} homework`}
                                                        value={lesson.homework}
                                                        onChange={(event) => updateLessonField(index, 'homework', event.target.value)}
                                                        rows={4}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                        <tr>
                                            <th>Assessment</th>
                                            {plan.lessons.map((lesson, index) => (
                                                <td key={`lesson-assessment-${lesson.lessonNumber}`}>
                                                    <textarea
                                                        aria-label={`Lesson ${lesson.lessonNumber} assessment`}
                                                        value={lesson.assessment}
                                                        onChange={(event) => updateLessonField(index, 'assessment', event.target.value)}
                                                        rows={4}
                                                    />
                                                </td>
                                            ))}
                                        </tr>
                                        <tr className="lesson-plan-worksheet-row">
                                            <th>Worksheet</th>
                                            {plan.lessons.map((lesson) => (
                                                <td key={`lesson-worksheet-${lesson.lessonNumber}`}>
                                                    <button
                                                        type="button"
                                                        className="lesson-plan-worksheet-btn"
                                                        onClick={() => createWorksheetFromLesson(lesson)}
                                                    >
                                                        Create worksheet
                                                    </button>
                                                </td>
                                            ))}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </section>
                ) : (
                    <section className="lesson-plan-empty">
                        Generate a lesson plan to preview the editable weekly table here.
                    </section>
                )}
            </div>
        </div>
    );
}
